/**
 * 中国象棋AI逻辑 - 优化版本
 * 基于启发式评估和高效决策的智能AI，避免深度搜索导致的卡顿
 */

import { XiangqiGameState, XiangqiMove, Position, XiangqiPiece, XiangqiPlayer, Difficulty, XIANGQI_PIECE_VALUES } from '../types/game';
import { getAllPossibleMoves, checkGameEnd, makeTestMove, isKingInCheck, isInPalace } from './xiangqi';

// 棋子基础价值
const getPieceValue = (piece: XiangqiPiece): number => {
  return XIANGQI_PIECE_VALUES[piece.type];
};

// 快速位置评估
const getQuickPositionValue = (piece: XiangqiPiece, position: Position): number => {
  let value = 0;

  switch (piece.type) {
    case 'general':
      // 将帅在九宫格中心更安全
      if (piece.player === 'red') {
        value += (9 - position.row) * 2;
      } else {
        value += position.row * 2;
      }
      break;

    case 'chariot':
      // 车在开放线路更有价值
      value += (9 - Math.abs(position.col - 4)) * 2;
      if (piece.player === 'red') {
        value += (9 - position.row);
      } else {
        value += position.row;
      }
      break;

    case 'cannon':
      // 炮在中心位置更有价值
      if (position.col >= 3 && position.col <= 5) value += 4;
      if (piece.player === 'red') {
        value += (9 - position.row) * 0.8;
      } else {
        value += position.row * 0.8;
      }
      break;

    case 'horse':
      // 马在中心位置更有价值
      if (position.col >= 2 && position.col <= 6 && position.row >= 2 && position.row <= 7) {
        value += 5;
      }
      break;

    case 'soldier':
      // 兵卒过河后价值递增
      if (piece.player === 'red') {
        if (position.row <= 4) { // 过河
          value += (5 - position.row) * 8;
        } else {
          value += 2;
        }
      } else {
        if (position.row >= 5) { // 过河
          value += (position.row - 4) * 8;
        } else {
          value += 2;
        }
      }
      // 兵卒在中心位置更有价值
      if (position.col >= 3 && position.col <= 5) value += 3;
      break;

    case 'advisor':
    case 'elephant':
      // 防守性棋子在中心位置更有价值
      if (position.col >= 3 && position.col <= 5) value += 3;
      break;
  }

  return value;
};

// 检查移动是否会导致将军
const doesMoveGiveCheck = (gameState: XiangqiGameState, move: XiangqiMove): boolean => {
  const testBoard = makeTestMove(gameState.board, move);
  const enemy = gameState.currentPlayer === 'red' ? 'black' : 'red';
  return isKingInCheck(enemy, testBoard);
};

// 检查移动是否会解除被将军状态
const doesMoveResolveCheck = (gameState: XiangqiGameState, move: XiangqiMove): boolean => {
  const testBoard = makeTestMove(gameState.board, move);
  return !isKingInCheck(gameState.currentPlayer, testBoard);
};

// 快速移动评分
const scoreMove = (gameState: XiangqiGameState, move: XiangqiMove): number => {
  let score = Math.random() * 10; // 基础随机性

  // 吃子奖励
  if (move.captures) {
    score += getPieceValue(move.captures) * 10;

    // 吃掉重要棋子的额外奖励
    if (move.captures.type === 'general') score += 10000;
    if (move.captures.type === 'chariot') score += 500;
    if (move.captures.type === 'cannon') score += 300;
  }

  // 将军奖励
  if (doesMoveGiveCheck(gameState, move)) {
    score += 200;
  }

  // 解除被将军状态奖励
  if (isKingInCheck(gameState.currentPlayer, gameState.board) &&
      doesMoveResolveCheck(gameState, move)) {
    score += 500;
  }

  // 位置价值改善
  const piece = gameState.board[move.from.row][move.from.col];
  if (piece) {
    const fromValue = getQuickPositionValue(piece, move.from);
    const toValue = getQuickPositionValue(piece, move.to);
    score += (toValue - fromValue);
  }

  // 中心控制奖励
  if (move.to.col >= 3 && move.to.col <= 5) {
    score += 20;
  }

  // 前进奖励（针对进攻性棋子）
  if (piece && ['chariot', 'cannon', 'soldier'].includes(piece.type)) {
    if (piece.player === 'red' && move.to.row < move.from.row) {
      score += 15;
    } else if (piece.player === 'black' && move.to.row > move.from.row) {
      score += 15;
    }
  }

  // 保护将帅
  if (piece && piece.type === 'general') {
    if (isInPalace(move.to, piece.player)) {
      score += 30; // 将帅留在九宫格内
    }
  }

  return score;
};

// 检查是否为必胜移动
const isForcedWin = (gameState: XiangqiGameState, move: XiangqiMove): boolean => {
  if (move.captures && move.captures.type === 'general') {
    return true;
  }
  return false;
};

// 检查是否为必败移动
const isForcedLoss = (gameState: XiangqiGameState, move: XiangqiMove): boolean => {
  const testBoard = makeTestMove(gameState.board, move);

  // 检查移动后是否会被吃掉将帅
  const enemy = gameState.currentPlayer === 'red' ? 'black' : 'red';
  const enemyMoves = getAllPossibleMoves({ ...gameState, board: testBoard, currentPlayer: enemy }, enemy);

  return enemyMoves.some(enemyMove =>
    enemyMove.captures && enemyMove.captures.type === 'general'
  );
};

// 主要AI决策函数 - 优化版本
export const getXiangqiAIDecision = (
  gameState: XiangqiGameState,
  difficulty: Difficulty = 'medium'
): XiangqiMove | null => {
  const moves = getAllPossibleMoves(gameState, gameState.currentPlayer);

  if (moves.length === 0) {
    return null;
  }

  // 检查游戏是否已经结束
  const gameEnd = checkGameEnd(gameState);
  if (gameEnd.isEnd) {
    return null;
  }

  // 根据难度选择策略
  switch (difficulty) {
    case 'easy':
      return getEasyMove(gameState, moves);
    case 'medium':
      return getMediumMove(gameState, moves);
    case 'hard':
      return getHardMove(gameState, moves);
    default:
      return getMediumMove(gameState, moves);
  }
};

// 简单模式 - 随机 + 基本策略
const getEasyMove = (gameState: XiangqiGameState, moves: XiangqiMove[]): XiangqiMove => {
  // 检查必胜移动
  const forcedWins = moves.filter(move => isForcedWin(gameState, move));
  if (forcedWins.length > 0) {
    return forcedWins[0];
  }

  // 避免必败移动
  const safeMoves = moves.filter(move => !isForcedLoss(gameState, move));

  // 优先吃子
  const captureMoves = safeMoves.filter(move => move.captures);
  if (captureMoves.length > 0) {
    return captureMoves[Math.floor(Math.random() * captureMoves.length)];
  }

  // 随机选择安全移动
  if (safeMoves.length > 0) {
    return safeMoves[Math.floor(Math.random() * safeMoves.length)];
  }

  // 如果没有安全移动，随机选择
  return moves[Math.floor(Math.random() * moves.length)];
};

// 中等模式 - 启发式评估
const getMediumMove = (gameState: XiangqiGameState, moves: XiangqiMove[]): XiangqiMove => {
  // 检查必胜移动
  const forcedWins = moves.filter(move => isForcedWin(gameState, move));
  if (forcedWins.length > 0) {
    return forcedWins[0];
  }

  // 避免必败移动
  const safeMoves = moves.filter(move => !isForcedLoss(gameState, move));

  // 评估所有安全移动
  const evaluatedMoves = safeMoves.map(move => ({
    move,
    score: scoreMove(gameState, move)
  }));

  // 按分数排序
  evaluatedMoves.sort((a, b) => b.score - a.score);

  // 在前几个最佳移动中随机选择（增加变化性）
  const topMoves = evaluatedMoves.slice(0, Math.min(3, evaluatedMoves.length));
  return topMoves[Math.floor(Math.random() * topMoves.length)].move;
};

// 困难模式 - 深度启发式评估
const getHardMove = (gameState: XiangqiGameState, moves: XiangqiMove[]): XiangqiMove => {
  // 检查必胜移动
  const forcedWins = moves.filter(move => isForcedWin(gameState, move));
  if (forcedWins.length > 0) {
    return forcedWins[0];
  }

  // 避免必败移动
  const safeMoves = moves.filter(move => !isForcedLoss(gameState, move));

  // 检查是否需要立即应将
  const inCheck = isKingInCheck(gameState.currentPlayer, gameState.board);
  const checkResolvingMoves = inCheck ?
    safeMoves.filter(move => doesMoveResolveCheck(gameState, move)) :
    safeMoves;

  // 评估移动（更详细的评分）
  const evaluatedMoves = (checkResolvingMoves.length > 0 ? checkResolvingMoves : safeMoves).map(move => {
    let score = scoreMove(gameState, move);

    // 额外的困难模式评估
    const testBoard = makeTestMove(gameState.board, move);
    const enemy = gameState.currentPlayer === 'red' ? 'black' : 'red';

    // 检查移动后对手的反击机会
    const enemyMoves = getAllPossibleMoves({ ...gameState, board: testBoard, currentPlayer: enemy }, enemy);
    const enemyCaptures = enemyMoves.filter(enemyMove => enemyMove.captures);
    const enemyChecks = enemyMoves.filter(enemyMove => {
      const enemyTestBoard = makeTestMove(testBoard, enemyMove);
      return isKingInCheck(gameState.currentPlayer, enemyTestBoard);
    });

    // 惩罚给对手机会的移动
    score -= enemyCaptures.length * 50;
    score -= enemyChecks.length * 100;

    // 奖励限制对手移动的走法
    score += Math.max(0, (20 - enemyMoves.length)) * 5;

    return { move, score };
  });

  // 按分数排序
  evaluatedMoves.sort((a, b) => b.score - a.score);

  // 选择最佳移动（困难模式更确定性）
  return evaluatedMoves[0].move;
};

// 优化的AI思考模拟 - 更短的等待时间
export const simulateXiangqiAIThinking = async (difficulty: Difficulty = 'medium'): Promise<void> => {
  let thinkingTime: number;

  switch (difficulty) {
    case 'easy':
      thinkingTime = 300 + Math.random() * 300; // 300-600ms
      break;
    case 'medium':
      thinkingTime = 500 + Math.random() * 500; // 500-1000ms
      break;
    case 'hard':
      thinkingTime = 800 + Math.random() * 700; // 800-1500ms
      break;
    default:
      thinkingTime = 600;
  }

  await new Promise(resolve => setTimeout(resolve, thinkingTime));
};

export default {
  getXiangqiAIDecision,
  simulateXiangqiAIThinking
};